import React, { Suspense, useEffect } from "react";

const Countdown = React.lazy(() => import("./Countdown"));
const ProductCompareTable = React.lazy(() => import("./ProductCompareTable"));
const ProductGrid = React.lazy(() => import("./ProductGrid"));
const UserCard = React.lazy(() => import("./UserCard"));

// CSS animations
const styles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slideInFromTop {
    from { opacity: 0; transform: translateY(-30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

const ComponentsContainer = ({ apiResult, showCard, showCountdown, color }) => {
  // Inject styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Check if we have actual API data (not just an empty object)
  const hasApiData = apiResult && Object.keys(apiResult).length > 0;
  
  if (!hasApiData && !showCard && !showCountdown) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: '200px', // Increased margin from top to avoid overlap with Agent
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 10,
      maxWidth: '90vw',
      maxHeight: 'calc(90vh - 200px)', // Adjust height to account for increased top margin
      overflow: 'auto',
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      animation: 'fadeIn 0.6s ease-out',
      padding: '20px',
      background: 'rgba(0, 0, 0, 0.1)',
      borderRadius: '15px',
      backdropFilter: 'blur(5px)'
    }}>
      {/* API Result Information */}
      {hasApiData && (
        <div style={{
          width: '100%',
          background: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '15px',
          padding: '1rem',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          marginBottom: '1rem',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            background: 'rgba(0, 0, 0, 0.25)',
            padding: '0.8rem',
            borderRadius: '10px',
            marginBottom: '0.8rem',
            border: '2px solid rgba(255, 255, 255, 0.4)'
          }}>
            <div style={{ fontSize: '0.9rem', opacity: '0.8', marginBottom: '0.3rem' }}>
              Detected Intent:
            </div>
            <div style={{ 
              fontSize: '1.2rem', 
              fontWeight: '700', 
              color: '#00ff88',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}>
              {apiResult.intent}
            </div>
          </div>
          
          {/* Original Text */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.2)',
            padding: '0.5rem',
            borderRadius: '8px',
            fontSize: '0.9rem',
            marginBottom: '0.8rem',
            fontStyle: 'italic'
          }}>
            <span style={{ opacity: '0.8' }}>Original: </span>
            "{apiResult.originalText}"
          </div>
          
          {/* Available Intents */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.2)',
            padding: '0.5rem',
            borderRadius: '8px',
            fontSize: '0.8rem',
            opacity: '0.7'
          }}>
            <span style={{ opacity: '0.8' }}>Available Intents: </span>
            {apiResult.availableIntents?.join(', ')}
          </div>
        </div>
      )}

      {/* Product Comparison Table - Show when intent is "Products Comparision" */}
      {hasApiData && apiResult.productComparisonData && (
        <Suspense fallback={
          <div style={{
            padding: '1rem',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '15px',
            color: 'white',
            textAlign: 'center',
            animation: 'pulse 1.5s ease-in-out infinite',
            marginBottom: '1rem'
          }}>
            Loading Product Comparison...
          </div>
        }>
          <ProductCompareTable productData={apiResult.productComparisonData} />
        </Suspense>
      )}

      {/* Product Grid - Always show when API result is available */}
      {hasApiData && apiResult.productGridData && (
        <Suspense fallback={
          <div style={{
            padding: '1rem',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '15px',
            color: 'white',
            textAlign: 'center',
            animation: 'pulse 1.5s ease-in-out infinite',
            marginBottom: '1rem'
          }}>
            Loading Product Grid...
          </div>
        }>
          <ProductGrid products={apiResult.productGridData.products || []} />
        </Suspense>
      )}
      
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        width: '100%',
        animation: 'fadeIn 0.8s ease-out 0.5s both'
      }}>
        {showCard && (
          <div style={{
            animation: 'slideInFromTop 0.6s ease-out'
          }}>
            <UserCard bgColor={color} />
          </div>
        )}
        
        {showCountdown && (
          <Suspense fallback={
            <div style={{
              padding: '1rem',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '15px',
              color: 'white',
              textAlign: 'center',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              Loading Countdown...
            </div>
          }>
            <Countdown start={10} />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default ComponentsContainer;
